import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Deep-link handler for pages that render rental cards.
 *
 * Reads `?rental=<id>` and optional `?tab=<name>` from the URL and:
 *   1. Calls `setTab(tab)` if a tab param is present.
 *   2. Waits for the DOM to settle after data has loaded, finds the card
 *      whose `data-rental-id` matches, scrolls it into view, and adds a
 *      short-lived `.rental-highlight` class so the eye lands on it.
 *   3. Strips the query params so a refresh doesn't re-trigger the effect.
 *
 * Callers must:
 *   - Add `data-rental-id={rental.id}` to the outermost element of each
 *     rendered rental card (the same element being scrolled to).
 *   - Ensure `.rental-highlight` is defined globally (see index.css).
 */
export const useDeepLinkedRental = ({ setTab, loading, ready }) => {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const rentalId = params.get("rental");
        const tab = params.get("tab");
        if (!rentalId && !tab) return;
        if (tab && typeof setTab === "function") {
            setTab(tab);
        }
        if (!rentalId) return;
        // Wait until data has loaded before we try to scroll.
        if (loading) return;
        if (typeof ready === "boolean" && !ready) return;
        // Give React one paint to render the newly-filtered tab.
        const t = setTimeout(() => {
            const el = document.querySelector(
                `[data-rental-id="${CSS.escape(rentalId)}"]`,
            );
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.classList.add("rental-highlight");
                setTimeout(() => el.classList.remove("rental-highlight"), 2400);
            }
            // Strip query params so a refresh doesn't loop the animation.
            navigate(location.pathname, { replace: true });
        }, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, loading, ready]);
};

export default useDeepLinkedRental;
